import { tenantDataService } from './tenantDataService';
import { appointmentService } from './appointmentService';

/**
 * Service for managing leave requests and their integration with appointments
 */
class LeaveService {
  /**
   * Update leave request status and sync with corresponding appointment
   * @param {string} leaveRequestId - ID of the leave request
   * @param {string} newStatus - New status (pending, approved, declined)
   * @param {string} adminNotes - Optional admin notes
   * @param {string} adminId - ID of admin making the change
   * @returns {Promise<Object>} Updated leave request
   */
  async updateLeaveStatus(leaveRequestId, newStatus, adminNotes = '', adminId = null) {
    try {
      // Get the leave request
      const leaveRequest = await tenantDataService.getById('leaveRequests', leaveRequestId);
      if (!leaveRequest) {
        throw new Error('Leave request not found');
      }

      // Prepare update data
      const updateData = {
        status: newStatus,
        updatedAt: new Date().toISOString(),
        ...(adminNotes && { adminNotes }),
        ...(adminId && { reviewedBy: adminId, reviewedAt: new Date().toISOString() })
      };

      // Update leave request
      const updatedLeaveRequest = await tenantDataService.update('leaveRequests', leaveRequestId, updateData);

      // Find and update corresponding appointment if it exists
      if (leaveRequest.appointmentId) {
        const appointmentStatus = this.mapLeaveStatusToAppointmentStatus(newStatus);
        await appointmentService.updateAppointment(leaveRequest.appointmentId, {
          status: appointmentStatus,
          updatedAt: new Date().toISOString()
        });
      } else {
        // Find appointment by matching criteria
        const appointments = await tenantDataService.getAll('appointments');
        console.log('🔍 LeaveService: Looking for matching appointment...');
        console.log('🔍 LeaveService: Leave request data:', {
          staffId: leaveRequest.staffId,
          startDate: leaveRequest.startDate,
          type: leaveRequest.type,
          leaveRequestId: leaveRequestId
        });
        console.log('🔍 LeaveService: Available appointments:', appointments.length);
        
        // Primary strategy: Find by leaveRequestId (most reliable)
        let matchingAppointment = appointments.find(apt => 
          apt.leaveRequestId === leaveRequestId
        );

        // Fallback strategy: Find by matching criteria
        if (!matchingAppointment) {
          matchingAppointment = appointments.find(apt =>
            apt.staffId === leaveRequest.staffId &&
            apt.date === leaveRequest.startDate &&
            (apt.eventType === leaveRequest.type || 
             apt.eventType === 'leave' || 
             apt.eventType === 'annual_leave') &&
            (apt.type === 'ooo' || apt.type === 'event')
          );
        }

        console.log('🔍 LeaveService: Matching appointment found:', matchingAppointment ? 'YES' : 'NO');
        if (matchingAppointment) {
          console.log('🔍 LeaveService: Matching appointment details:', {
            id: matchingAppointment.id,
            staffId: matchingAppointment.staffId,
            date: matchingAppointment.date,
            eventType: matchingAppointment.eventType,
            currentStatus: matchingAppointment.status
          });
          
          const appointmentStatus = this.mapLeaveStatusToAppointmentStatus(newStatus);
          console.log('🔍 LeaveService: Updating appointment status from', matchingAppointment.status, 'to', appointmentStatus);
          
          await appointmentService.updateAppointment(matchingAppointment.id, {
            status: appointmentStatus,
            updatedAt: new Date().toISOString()
          });
          
          console.log('✅ LeaveService: Appointment status updated successfully');
        } else {
          console.log('❌ LeaveService: No matching appointment found for leave request');
        }
      }

      return updatedLeaveRequest;
    } catch (error) {
      console.error('Error updating leave status:', error);
      throw error;
    }
  }

  /**
   * Map leave request status to appointment status
   * @param {string} leaveStatus - Leave request status
   * @returns {string} Corresponding appointment status
   */
  mapLeaveStatusToAppointmentStatus(leaveStatus) {
    const statusMap = {
      'pending': 'pending',
      'approved': 'confirmed',
      'declined': 'cancelled'
    };
    return statusMap[leaveStatus] || 'pending';
  }

  /**
   * Get all leave requests for a staff member
   * @param {string} staffId - Staff member ID
   * @param {Object} filters - Optional filters (status, type, dateRange)
   * @returns {Promise<Array>} Array of leave requests
   */
  async getStaffLeaveRequests(staffId, filters = {}) {
    try {
      const allLeaveRequests = await tenantDataService.getAll('leaveRequests');
      let staffRequests = allLeaveRequests.filter(request => request.staffId === staffId);

      // Apply filters
      if (filters.status) {
        staffRequests = staffRequests.filter(request => request.status === filters.status);
      }

      if (filters.type) {
        staffRequests = staffRequests.filter(request => request.type === filters.type);
      }

      if (filters.dateRange) {
        const { start, end } = filters.dateRange;
        staffRequests = staffRequests.filter(request => {
          const requestStart = new Date(request.startDate);
          const requestEnd = new Date(request.endDate);
          return requestStart >= start && requestEnd <= end;
        });
      }

      // Sort by start date (most recent first)
      return staffRequests.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    } catch (error) {
      console.error('Error getting staff leave requests:', error);
      throw error;
    }
  }

  /**
   * Get all pending leave requests for admin review
   * @returns {Promise<Array>} Array of pending leave requests
   */
  async getPendingLeaveRequests() {
    try {
      const allLeaveRequests = await tenantDataService.getAll('leaveRequests');
      return allLeaveRequests
        .filter(request => request.status === 'pending')
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)); // Oldest first
    } catch (error) {
      console.error('Error getting pending leave requests:', error);
      throw error;
    }
  }

  /**
   * Delete a leave request and its corresponding appointment
   * @param {string} leaveRequestId - ID of the leave request to delete
   * @returns {Promise<boolean>} Success status
   */
  async deleteLeaveRequest(leaveRequestId) {
    try {
      // Get the leave request first
      const leaveRequest = await tenantDataService.getById('leaveRequests', leaveRequestId);
      if (!leaveRequest) {
        throw new Error('Leave request not found');
      }

      // Delete corresponding appointment if it exists
      if (leaveRequest.appointmentId) {
        await appointmentService.deleteAppointment(leaveRequest.appointmentId);
      } else {
        // Find and delete appointment by matching criteria
        const appointments = await tenantDataService.getAll('appointments');
        const matchingAppointment = appointments.find(apt =>
          apt.staffId === leaveRequest.staffId &&
          apt.date === leaveRequest.startDate &&
          apt.eventType === leaveRequest.type &&
          apt.leaveRequestId === leaveRequestId
        );

        if (matchingAppointment) {
          await appointmentService.deleteAppointment(matchingAppointment.id);
        }
      }

      // Delete the leave request
      await tenantDataService.delete('leaveRequests', leaveRequestId);
      return true;
    } catch (error) {
      console.error('Error deleting leave request:', error);
      throw error;
    }
  }

  /**
   * Create a new leave request with corresponding appointment
   * @param {Object} leaveRequestData - Leave request data
   * @param {Object} appointmentData - Corresponding appointment data
   * @returns {Promise<Object>} Created leave request with appointment ID
   */
  async createLeaveRequest(leaveRequestData, appointmentData) {
    try {
      // Create the leave request first
      const leaveRequest = await tenantDataService.create('leaveRequests', leaveRequestData);

      // Create corresponding appointment with leave request ID
      const appointment = await appointmentService.createAppointment({
        ...appointmentData,
        leaveRequestId: leaveRequest.id
      });

      // Update leave request with appointment ID
      const updatedLeaveRequest = await tenantDataService.update('leaveRequests', leaveRequest.id, {
        appointmentId: appointment.id
      });

      return updatedLeaveRequest;
    } catch (error) {
      console.error('Error creating leave request:', error);
      throw error;
    }
  }

  /**
   * Get leave statistics for a staff member
   * @param {string} staffId - Staff member ID
   * @param {Object} leaveSettings - Leave settings from admin
   * @returns {Promise<Object>} Leave statistics
   */
  async getLeaveStatistics(staffId, leaveSettings = {}) {
    try {
      const leaveRequests = await this.getStaffLeaveRequests(staffId);
      const currentYear = new Date().getFullYear();

      // Filter for current leave year
      const leaveYearStart = leaveSettings.leaveYearStartMonth || 1;
      const leaveYearStartDay = leaveSettings.leaveYearStartDay || 1;
      const yearStart = new Date(currentYear, leaveYearStart - 1, leaveYearStartDay);
      const yearEnd = new Date(currentYear + 1, leaveYearStart - 1, leaveYearStartDay - 1);

      const currentYearRequests = leaveRequests.filter(request => {
        const requestDate = new Date(request.startDate);
        return requestDate >= yearStart && requestDate <= yearEnd;
      });

      // Calculate statistics by type
      const stats = {
        leave: { used: 0, pending: 0, total: 20 },
        sick: { used: 0, pending: 0, total: 10 },
        maternity: { used: 0, pending: 0, total: 90 },
        training: { used: 0, pending: 0, total: 5 },
        remote: { used: 0, pending: 0, total: 52 }
      };

      currentYearRequests.forEach(request => {
        const days = request.daysCount || 1;
        const type = request.type;

        if (stats[type]) {
          if (request.status === 'approved') {
            stats[type].used += days;
          } else if (request.status === 'pending') {
            stats[type].pending += days;
          }
        }
      });

      // Calculate remaining days
      Object.keys(stats).forEach(type => {
        stats[type].remaining = Math.max(0, stats[type].total - stats[type].used - stats[type].pending);
      });

      return {
        leaveYear: { start: yearStart, end: yearEnd },
        stats,
        totalRequests: currentYearRequests.length,
        pendingRequests: currentYearRequests.filter(r => r.status === 'pending').length
      };
    } catch (error) {
      console.error('Error getting leave statistics:', error);
      throw error;
    }
  }
}

export const leaveService = new LeaveService();
